import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePagination from '../../hooks/usePagination';
import { getAllBDVendorsDetails } from '../../store/actions/settingsActionCreator';
import { HUBS_ACTIVE_INLINE_LIMIT, preProcessPayload } from './vendor.helper';
import { Box, Typography } from '@material-ui/core';
import useVendorManagementStyles from './useVendorManagementStyles';
import TruncatedList from '../../components/TruncatedList';
import { getPermissions } from '../../utils/permissions';

const useVendorManagement = (props: any) => {
    const dispatch = useDispatch();
    const initialPage = 0; // Start on page 0 (0-based index)
    const initialPageSize = 25; // Show 25 rows per page
    const initialActiveFilterCard = {
        type: 'Total',
        subType: '',
    };
    const initialFilterValues = {};
    const initialSearchText = '';
    const initialPayload = {
        pageNo: initialPage,
        pageSize: initialPageSize,
        searchText: '',
        ...props?.externalFiltered,
    };
    // Search is committed explicitly (see `handleSearchSubmit`). Without this,
    // usePagination rebuilt `payload` on every `searchTextValue` change and
    // Searchbar pushed one per keystroke, so typing fired a request per
    // character. It also removes the post-mount payload rewrite.
    const disableAutoSearch = true;
    const {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        activeFilterCard,
        filterValues,
        searchTextValue,
        payload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        setFilterValues,
        clearSearch,
        onManualSearch,
        onManualSearchClear,
    } = usePagination(
        initialPage,
        initialPageSize,
        initialActiveFilterCard,
        initialFilterValues,
        initialSearchText,
        initialPayload,
        disableAutoSearch,
    );
    const [loading, setLoading] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [isOpen, toggleModal] = useState(false);
    const [openBulkModal, setBulkModalOpen] = useState(false);
    const [bulkUploadType, setBulkUploadType] = useState(null);

    // Narrow selectors — subscribing to the whole root state re-renders every
    // row on any unrelated dispatch, because each reducer returns a new root.
    const vendorData: any = useSelector(
        (store: any) => store?.settings?.bdVendorsList,
    );
    const styles: any = useVendorManagementStyles();

    const vendors: any[] = useMemo(
        () => (Array.isArray(vendorData?.vendors) ? vendorData.vendors : []),
        [vendorData],
    );
    const totalCount: number = vendorData?.totalCount ?? 0;
    const totalPages: number = vendorData?.totalPages ?? 0;

    const permissions = useMemo(() => getPermissions('vendor_management'), []);

    const dispatchGetVendors = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            dispatch(getAllBDVendorsDetails(data, onSuccess, onError));
        },
        [dispatch],
    );

    // There is no request cancellation in the API middleware, so tag every
    // request and let only the latest one settle the loading flag.
    const requestIdRef = useRef(0);

    const fetchVendorsData = useCallback(() => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        const preProceedPayload = preProcessPayload(payload);
        const settle = () => {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        };
        dispatchGetVendors(preProceedPayload, settle, settle);
    }, [payload, dispatchGetVendors]);

    // usePagination flips `firstRenderDone` right after mount, which rewrites
    // `payload` into a fresh object with identical content. Guard against that
    // by fetching only when the payload's content actually changes, so the
    // initial load fires exactly once.
    const lastFetchSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        const signature = JSON.stringify(payload);
        if (lastFetchSignatureRef.current === signature) return;
        lastFetchSignatureRef.current = signature;
        fetchVendorsData();
    }, [payload, fetchVendorsData]);

    // Rows may have changed while a modal was open, so bypass the signature
    // guard and refetch the current page.
    const refetchVendorsData = useCallback(() => {
        lastFetchSignatureRef.current = null;
        fetchVendorsData();
    }, [fetchVendorsData]);

    const appliedSearchText = payload?.searchText ?? '';
    const isSearchDirty =
        (searchTextValue ?? '').trim() !== (appliedSearchText ?? '').trim();

    const handleSearchSubmit = () => {
        onManualSearch(searchTextValue);
    };

    const handleSearchClear = () => {
        clearSearch();
        onManualSearchClear();
    };

    // Stable identity — it is passed to the memoized VendorRow, so a fresh
    // closure per render would defeat React.memo on every row.
    const handleRowClick = useCallback((e: any, val: any) => {}, []);

    const getColumnComponentValue = useCallback(
        (row: any, column: any, handleAction: any) => {
            switch (column?.mappingKey) {
                case 'hubsActive':
                    return (
                        <TruncatedList
                            items={row?.hubsActive}
                            limit={HUBS_ACTIVE_INLINE_LIMIT}
                            title={`Hubs Active${
                                row?.name ? ` — ${row.name}` : ''
                            }`}
                        />
                    );

                default:
                    return (
                        <Box>
                            <Typography className={styles.tableValue}>
                                {row?.[column?.mappingKey] ?? '-'}
                            </Typography>
                        </Box>
                    );
            }
        },
        [styles],
    );

    const handleSingleRidersPage = (rider: any) => {};

    const openVendorModal = (vendor?: any) => {
        setSelectedVendor(vendor);
        toggleModal(true);
    };
    const closeVendorModal = () => {
        toggleModal(false);
        refetchVendorsData();
        setSelectedVendor(null);
    };

    const openBulkUploadModal = (type?: any) => {
        setBulkUploadType(type);
        setBulkModalOpen(true);
    };
    const closeBulkUploadModal = () => {
        setBulkModalOpen(false);
        refetchVendorsData();
        setBulkUploadType(null);
    };

    return {
        handleRowClick,
        handleSingleRidersPage,
        getColumnComponentValue,
        vendors,
        totalCount,
        totalPages,
        pagination,
        handleChangeRowsPerPage,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        activeFilterCard,
        filterValues,
        setFilterValues,
        searchTextValue,
        permissions,
        loading,
        styles,
        handleChangePage,
        openVendorModal,
        closeVendorModal,
        selectedVendor,
        isOpen,
        payload,
        fetchVendorsData,
        refetchVendorsData,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    };
};

export default useVendorManagement;
